// import { useState, useEffect } from "react";
// import { UserContext } from "./UserContext";

// import { jwtDecode } from "jwt-decode";
// //import { decodeJwt } from "../../utils/Helpers";

// function UserContextProvider({ children }) {
//   const [user, setUser] = useState(null);
//   const [decodedUser, setDecodedUser] = useState(null);
//   const [isLoggedIn, setIsLoggedIn] = useState(false);

//   useEffect(() => {
//     const extractUser = () => {
//       const storedUser = localStorage.getItem("token");
//       if (storedUser) {
//         setUser(storedUser);
//         setDecodedUser(jwtDecode(storedUser));
//         setIsLoggedIn(true);
//       }
//     };
//     extractUser();
//   }, []);

//   const handleLogin = (userData) => {
//     localStorage.setItem("token", userData);
//     setUser(userData);
//     setDecodedUser(jwtDecode(userData));
//     setIsLoggedIn(true);
//   };

//   const handleLogout = () => {
//     setUser(null);
//     setDecodedUser(null);
//     setIsLoggedIn(false);
//   };

//   return (
//     <UserContext.Provider
//       value={{
//         user,
//         setUser: setUser,
//         isLoggedIn,
//         decodedUser,
//         setDecodedUser: setDecodedUser,
//         setIsLoggedIn: setIsLoggedIn,
//         login: handleLogin,
//         logout: handleLogout,
//       }}
//     >
//       {children}
//     </UserContext.Provider>
//   );
// }
// export default UserContextProvider;
// services/contexts/UserContextProvider.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { UserContext } from "./UserContext";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

function UserContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [decodedUser, setDecodedUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sessionCheckIntervalRef = useRef(null);
  
  // Flag to track if logout is in progress
  const isLoggingOutRef = useRef(false);

  // Check if token is valid
  const isTokenValid = useCallback((token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp < currentTime) {
        console.log('Token expired');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }, []);

  // Show session expired message
  const showSessionExpiredMessage = useCallback(() => {
    // Don't show if already logging out
    if (isLoggingOutRef.current) {
      return;
    }
    
    toast.error("THE SESSION MAY HAVE EXPIRED, PLEASE LOG OUT AND LOG IN AGAIN!", {
      position: "top-right",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
      toastId: 'session-expired'
    });
  }, []);

  // Clear all auth data
  const clearAuthData = useCallback(() => {
    console.log('Clearing all auth data');
    
    // Clear all storage
    localStorage.removeItem('token');
    localStorage.removeItem('user-id');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user-id');
    sessionStorage.removeItem('session_active');
    sessionStorage.removeItem('session_start');
    sessionStorage.removeItem('manual_logout');
    sessionStorage.removeItem('session_expired');
    
    // Update state
    setUser(null);
    setDecodedUser(null);
    setIsLoggedIn(false);
  }, []);

  // Handle automatic logout (session expiration)
  const handleAutomaticLogout = useCallback(() => {
    console.log('Automatic logout due to session expiration');
    
    // Set logging out flag
    isLoggingOutRef.current = true;
    
    // Clear all data
    clearAuthData();
    
    // Show session expired message
    showSessionExpiredMessage();
    
    // Set flag for login page
    sessionStorage.setItem('session_expired', 'true');
    
    // Stop monitoring
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
    
    // Reset logging out flag after a delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
    
    // Redirect to login
    window.location.href = '/';
  }, [clearAuthData, showSessionExpiredMessage]);

  // Handle manual logout (user clicked logout button)
  const handleManualLogout = useCallback(async () => {
    console.log('Manual logout initiated by user');
    
    // Set logging out flag
    isLoggingOutRef.current = true;
    
    // Set manual logout flag
    sessionStorage.setItem('manual_logout', 'true');
    
    // Stop monitoring
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
    
    // Call logout API if needed
    try {
      const userId = localStorage.getItem('user-id') || sessionStorage.getItem('user-id');
      if (userId) {
        console.log('Logout API call would happen here for user:', userId);
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
    
    // Clear all data
    clearAuthData();
    
    // Reset logging out flag after a delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
    
    // Redirect to login
    window.location.href = '/';
  }, [clearAuthData]);

  // Start monitoring session
  const startSessionMonitoring = useCallback(() => {
    // Clear any existing interval
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }
    
    // Check every 30 seconds
    const interval = setInterval(() => {
      // Don't check if logout is in progress
      if (isLoggingOutRef.current) {
        return;
      }
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (token) {
        const isValid = isTokenValid(token);
        if (!isValid) {
          console.log('Token expired during session check');
          handleAutomaticLogout();
        }
      } else if (isLoggedIn) {
        // No token but logged in state is true - inconsistency
        console.log('State inconsistency - logged in but no token');
        handleAutomaticLogout();
      }
    }, 30000); // Check every 30 seconds
    
    sessionCheckIntervalRef.current = interval;
  }, [isTokenValid, handleAutomaticLogout, isLoggedIn]);

  // Stop monitoring
  const stopSessionMonitoring = useCallback(() => {
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
  }, []);

  // Initialize on component mount
  useEffect(() => {
    const initializeAuth = () => {
      // Check if this is a manual logout redirect
      const isManualLogout = sessionStorage.getItem('manual_logout') === 'true';
      
      if (isManualLogout) {
        console.log('Manual logout detected, clearing session expired flag');
        sessionStorage.removeItem('session_expired');
        sessionStorage.removeItem('manual_logout');
        return;
      }
      
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      
      if (token) {
        const isValid = isTokenValid(token);
        
        if (isValid) {
          setUser(token);
          setDecodedUser(jwtDecode(token));
          setIsLoggedIn(true);
          
          // Start monitoring session
          startSessionMonitoring();
          
          console.log('User authenticated from storage');
        } else {
          // Token expired - automatic logout
          console.log('Stored token expired on init');
          handleAutomaticLogout();
        }
      }
    };
    
    initializeAuth();
    
    // Cleanup on unmount
    return () => {
      stopSessionMonitoring();
    };
  }, [isTokenValid, startSessionMonitoring, stopSessionMonitoring, handleAutomaticLogout]);

  // Listen for storage events (multi-tab support)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'token' && !event.newValue) {
        // Token was removed in another tab
        const isManualLogout = sessionStorage.getItem('manual_logout') === 'true';
        
        if (!isManualLogout && !isLoggingOutRef.current) {
          console.log('Token removed in another tab - logging out');
          handleAutomaticLogout();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleAutomaticLogout]);

  const handleLogin = (userData) => {
    console.log('Login initiated');
    
    // Clear any existing flags
    sessionStorage.removeItem('session_expired');
    sessionStorage.removeItem('manual_logout');
    
    // Reset logging out flag
    isLoggingOutRef.current = false;
    
    // Store token in both storages for redundancy
    localStorage.setItem("token", userData);
    sessionStorage.setItem("token", userData);
    
    try {
      const decoded = jwtDecode(userData);
      setUser(userData);
      setDecodedUser(decoded);
      setIsLoggedIn(true);
      
      // Start monitoring session
      startSessionMonitoring();
      
      console.log('Login successful');
    } catch (error) {
      console.error('Error decoding token during login:', error);
    }
  };

  const contextValue = {
    user,
    setUser,
    isLoggedIn,
    decodedUser,
    setDecodedUser,
    setIsLoggedIn,
    login: handleLogin,
    logout: handleManualLogout,
    isTokenValid,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

// ✅ IMPORTANT: Default export
export default UserContextProvider;

// Named export if needed elsewhere
export { setupAxiosInterceptors } from '../api/axiosInterceptor';